<template>
  <AppFilterNavigation
    v-model:filters="filters"
    :ignore-list="ignoreList"
    :filter-name="filterName"
    @reset-filter="storage = []"
  >
    <v-col cols="12" md="2">
      <AppSelectDiscipline
        v-model="filters.disciplineIds"
        :input-props="{
          multiple: true
        }"
        data-test="disciplines"
      />
    </v-col>

    <v-col cols="12" md="2">
      <AppSelect
        v-model="filters.competitionLevelIds"
        :input-props="{
          label: $t(
            'calendar:filters.CompetitionLevels.label',
            'Competition levels'
          ),
          items: masterCompetitionLevels,
          itemValue: 'id',
          itemTitle: 'name',
          multiple: true,
          clearable: true
        }"
        data-test="competitionLevels"
      />
    </v-col>

    <v-col
      cols="12"
      md="2"
      class="px-1 mb-2 text-center hide-menu-element"
      style="position: relative"
    >
      <AppSelectMultipleDate
        v-model:date-from="filters.competitionStartDateFrom"
        v-model:date-until="filters.competitionStartDateUntil"
        data-test="dates"
        ignore-default-date
        clearable
        :date-options="years"
        :input-props="{
          label: $t(
            'calendar:filters.time.label',
            'Time',
            'Label of the time filter'
          )
        }"
      />
    </v-col>

    <v-col cols="12" md="2">
      <AppSelect
        v-model="storage"
        :input-props="{
          label: $t('common:results.filters.PlacedAndRankings.label'),
          items: [
            {
              value: 'isPlaced',
              text: $t('common:results.filters.IsPlaced.label')
            },
            {
              value: 'hasPoints',
              text: $t('common:results.filters.HasPoints.label')
            },
            {
              value: 'satisfiesQualification',
              text: $t('common:results.filters.IsQualified.label')
            }
          ],
          multiple: true,
          clearable: true
        }"
        data-test="storage"
      />
    </v-col>

    <v-col v-if="$vuetify.display.mdAndUp" cols="12" md="4">
      <AppSearchBox v-model="filters.keyword" clearable />
    </v-col>

    <template #additionals>
      <v-col cols="12" md="3">
        <div>
          <AppSelect
            v-model="filters.eventIsHighlighted"
            :input-props="{
              label: $t(
                'calendar:filters.StarShows.label',
                'Star Shows',
                'Label of the star shows filter'
              ),
              items: [
                {
                  value: false,
                  text: $t(
                    'calendar:filters.StarShows.no.label',
                    'No',
                    'Label of the star shows filter if not a star show'
                  )
                },
                {
                  value: true,
                  text: $t(
                    'calendar:filters.StarShows.yes.label',
                    'Yes',
                    'Label of the star shows filter if it is a star show'
                  )
                }
              ],
              clearable: true
            }"
            data-test="highlighted"
          />
        </div>
        <div>
          <AppSelectCup
            v-model="filters.cupIds"
            :filters="{
              disciplineIds: filters.disciplineIds
            }"
            group-by-disciplines
            :input-props="{
              multiple: true,
              appendInnerIcon: '$iconSearch'
            }"
            data-test="cups"
          />
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div>
          <AppSelectDifficultyLevel
            v-model="filters.difficultyLevelIds"
            :input-props="{
              label: $t('common:filters.difficultyLevels.label'),
              multiple: true,
              appendInnerIcon: '$iconSearch'
            }"
          />
        </div>
        <div>
          <AppSelectEventcode
            v-model="filters.eventcodeIds"
            :input-props="{
              label: $t('common:filters.eventCodes.label'),
              multiple: true,
              appendInnerIcon: '$iconSearch'
            }"
            :discipline-ids="filters.disciplineIds"
          />
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div>
          <AppSelectPlace
            v-model="filters.eventPlace"
            :input-props="{
              clearable: true
            }"
            data-test="place"
          />
        </div>
        <div>
          <AppSelectOrganisation
            v-model="filters.organiserIds"
            :input-props="{
              label: $t(
                'calendar:filters.Organisers.label',
                'Organisers',
                'Label of the Organisers filter'
              ),
              multiple: true
            }"
          />
        </div>
        <div>
          <AppSelect
            v-model="filters.inTeamCompetition"
            :input-props="{
              label: $t('common:filters.isTeam.label'),
              items: [
                {
                  value: false,
                  text: $t('common:no.label', 'No')
                },
                {
                  value: true,
                  text: $t('common:yes.label', 'Yes')
                }
              ],
              clearable: true
            }"
            data-test="inTeamCompetition"
          />
        </div>
      </v-col>
    </template>

    <template #mobile>
      <AppSearchBox v-model="filters.keyword" class="px-4" clearable />
    </template>
  </AppFilterNavigation>
</template>
<script>
import { useCompetitionLevelHelper } from "@composables/masterdata/competitions/competitionLevels";

import AppFilterNavigation from "@/components/elements/AppFilterNavigation.vue";

import AppSelectOrganisation from "@/components/elements/AppSelects/AppSelectOrganisation.vue";
import AppSelectDiscipline from "@AppSelects/AppSelectDiscipline.vue";
import AppSelectMultipleDate from "@/components/elements/AppSelects/AppSelectMultipleDate.vue";
import AppSelectPlace from "@/components/elements/AppSelects/AppSelectPlace.vue";
import AppSelectCup from "@/components/elements/AppSelects/AppSelectCup.vue";
import AppSelectEventcode from "@AppSelects/AppSelectEventcode.vue";
import AppSelectDifficultyLevel from "@AppSelects/AppSelectDifficultyLevel.vue";

import AppSearchBox from "@/components/elements/AppSearchBox.vue";
import AppSelect from "@AppSelects/AppSelect.vue";

export const QUERY_FILTERS = `
  $keyword: String,
  $disciplineIds: [ID!],
  $competitionLevelIds: [ID],
  $organiserIds: [ID],
  $eventPlace: String,
  $difficultyLevelIds: [ID],
  $competitionStartDateFrom: Date,
  $competitionStartDateUntil: Date,
  $eventIsHighlighted: Boolean,
  $cupIds: [ID]
  $eventcodeIds: [ID]
  $isPlaced: Boolean
  $hasPoints: Boolean
  $inTeamCompetition: Boolean
  $satisfiesQualification: Boolean
`;

export const QUERY_FILTER_VALUES = `
  keyword: $keyword,
  disciplineIds: $disciplineIds,
  competitionLevelIds: $competitionLevelIds,
  organiserIds: $organiserIds,
  difficultyLevelIds: $difficultyLevelIds,
  eventPlace: $eventPlace,
  competitionStartDateFrom: $competitionStartDateFrom,
  competitionStartDateUntil: $competitionStartDateUntil,
  eventIsHighlighted: $eventIsHighlighted
  cupIds: $cupIds
  eventcodeIds: $eventcodeIds,
  isPlaced: $isPlaced
  hasPoints: $hasPoints
  inTeamCompetition: $inTeamCompetition
  satisfiesQualification: $satisfiesQualification
`;

export default {
  name: "AppCompetitionResultsFilter",
  components: {
    AppSelectOrganisation,
    AppFilterNavigation,
    AppSelect,
    AppSelectMultipleDate,
    AppSelectPlace,
    AppSelectCup,
    AppSearchBox,
    AppSelectDiscipline,
    AppSelectEventcode,
    AppSelectDifficultyLevel
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    filterName: {
      type: String,
      required: true
    }
  },
  emits: ["update:modelValue"],
  setup() {
    return {
      ...useCompetitionLevelHelper()
    };
  },
  data() {
    return {
      additionals: []
    };
  },
  computed: {
    years() {
      const currentYear = Number(this.$dayjs().format("YYYY"));
      return [currentYear, currentYear - 1]
        .map(year => {
          return {
            type: "year",
            year: year
          };
        })
        .filter(x => x.year != 2019);
    },
    /*defaultDateUntil() {
      return [this.$dayjs().format("YYYY") + "-12-31"];
    },
    defaultDateFrom() {
      return [this.$dayjs().format("YYYY") + "-01-01"];
    },*/
    filters: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      }
    },
    storage: {
      get() {
        return this.additionals;
      },
      set(v) {
        this.additionals = v;
        this.filters.hasPoints = v.includes("hasPoints") || null;
        this.filters.isPlaced = v.includes("isPlaced") || null;
        this.filters.satisfiesQualification =
          v.includes("satisfiesQualification") || null;
      }
    },
    ignoreList() {
      return ["competitionStartDateUntil", "keyword"];
    }
  }
};
</script>
